import { useState } from "react";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Collapse from "@mui/material/Collapse";
import Tooltip from "@mui/material/Tooltip";
import { useNavigate } from "react-router-dom";
import type { TaskListItem } from "./useTaskListSubscription";
import { AnchorsSubTable } from "./AnchorsSubTable";
import { PUBLICATION_TYPE_LABELS } from "./TaskDataGrid";
import { PublicationSuccessRate } from "./PublicationSuccessRate";
import { TASK_TYPE_LABELS } from "./TaskDataGrid";

export function TaskRow(props: { item: TaskListItem }) {
  const { item } = props;
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const hasAnchors = item.anchors.length > 0;
  const isHomepage = item.taskType === "homepage_publication" || item.taskType === "sitewide_publication";
  const detailPath = `/tasks/${item.id}/show`;
  const detailHref = `#${detailPath}`;

  const handleRowClick = (event: React.MouseEvent<HTMLElement>) => {
    if (event.ctrlKey || event.metaKey) {
      window.open(detailHref, "_blank", "noopener,noreferrer");
      return;
    }
    void navigate(detailPath);
  };

  const handleAuxClick = (event: React.MouseEvent<HTMLElement>) => {
    if (event.button === 1) {
      event.preventDefault();
      window.open(detailHref, "_blank", "noopener,noreferrer");
    }
  };


  // Строка ведёт на карточку: с клавиатуры её открывают Enter'ом или пробелом,
  // как и мышью.
  const handleRowKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      void navigate(detailPath);
    }
  };

  return (
    <>
      <TableRow
        role="row"
        tabIndex={0}
        onKeyDown={handleRowKeyDown}
        hover
        sx={{
          cursor: "pointer",
          opacity: item.deactivatedAt !== null ? 0.5 : 1,
          color: item.deactivatedAt !== null ? "text.secondary" : "inherit",
          "&:hover .open-in-new-btn": { opacity: 1 },
        }}
        onClick={handleRowClick}
        onAuxClick={handleAuxClick}
      >
        <TableCell padding="none" sx={{ width: 40 }}>
          {hasAnchors && (
            <IconButton size="small" onClick={(event) => { event.stopPropagation(); setExpanded(!expanded); }}>
              {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{TASK_TYPE_LABELS[item.taskType]}</TableCell>
        <TableCell>{item.targetUrl}</TableCell>
        <TableCell>{item.language}</TableCell>
        <TableCell>{item.region}</TableCell>
        <TableCell align="right">{item.anchors.length}</TableCell>
        <TableCell>{item.phase}</TableCell>
        <TableCell>
          <span>{item.phaseStatus}</span>
          {item.phase === "publication" && item.publication !== null && (
            <PublicationSuccessRate publication={item.publication} phaseStatus={item.phaseStatus} />
          )}
        </TableCell>
        <TableCell>
          {item.publication !== null ? PUBLICATION_TYPE_LABELS[item.publication.type] : "—"}
        </TableCell>
        <TableCell>{new Date(item.createdAt).toLocaleString()}</TableCell>
        <TableCell>{item.createdByName}</TableCell>
        <TableCell>{item.lastPublishedAt ? new Date(item.lastPublishedAt).toLocaleString() : "—"}</TableCell>
        <TableCell>{item.comment ? item.comment.split(/\s+/).slice(0, 5).join(" ") : "—"}</TableCell>
        <TableCell padding="none" sx={{ width: 40 }}>
          <Tooltip title="Открыть в новой вкладке">
            <IconButton
              component="a"
              href={detailHref}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              className="open-in-new-btn"
              onClick={(event) => event.stopPropagation()}
              sx={{ opacity: 0, transition: "opacity 0.15s" }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
      {hasAnchors && (
        <TableRow>
          <TableCell colSpan={14} sx={{ py: 0, borderBottom: expanded ? undefined : "none" }}>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <AnchorsSubTable anchors={item.anchors} isHomepage={isHomepage} />
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
